import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { lerAzosPortal } from "@/components/atomic-crm/conexoes/AzosPortalConexao";

import { Card, CardContent } from "./comuns";
import { lerConexoes, type ConexaoPortal } from "./portalAzos";
import { useMeuUsuarioCrm } from "./portalAzosAcoes";

/**
 * Conexão com o portal da Azos (Conexões › Azos, migration 288), comum às
 * telas por função que usam o portal: Recebimentos, Cotações salvas, Exigências,
 * Propostas, Apólices e a página Azos. Cada um vê a própria conexão; super
 * admin escolhe a corretora. Ação que grava só na própria conexão (a function
 * não age em outra).
 */
export const useConexoesAzos = () => {
  const qc = useQueryClient();
  const [escolhida, setEscolhida] = useState<number | null>(null);
  const consulta = useQuery({ queryKey: ["azos-conexoes"], queryFn: lerConexoes, staleTime: 60_000 });
  const { data: meuId } = useMeuUsuarioCrm();
  const conexoes = consulta.data ?? null;
  const atual = conexoes?.find((c) => c.crm_user_id === escolhida) ?? conexoes?.[0] ?? null;
  const propria = conexoes?.find((c) => meuId != null && c.crm_user_id === meuId) ?? null;
  return {
    conexoes,
    atual,
    escolher: setEscolhida,
    erro: consulta.error ? (consulta.error as Error).message : null,
    recarregar: () => void qc.invalidateQueries({ queryKey: ["azos-conexoes"] }),
    /** Pode gravar no portal pela conexão em tela. */
    podeAgir: atual != null && atual.crm_user_id === propria?.crm_user_id && atual.situacao === "conectado",
    /** A própria conexão, conectada: base das leituras ao vivo (propostas, apólices, contrapropostas). */
    propriaConectada: propria?.situacao === "conectado" ? propria : null,
  };
};

export const SeletorConexao = ({
  conexoes,
  atual,
  aoEscolher,
}: {
  conexoes: ConexaoPortal[];
  atual: ConexaoPortal;
  aoEscolher: (crmUserId: number) => void;
}) => (
  <div className="flex flex-wrap items-center gap-2">
    {conexoes.length > 1 && (
      <Select value={String(atual.crm_user_id)} onValueChange={(v) => aoEscolher(Number(v))}>
        <SelectTrigger className="sm:w-80">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {conexoes.map((c) => (
            <SelectItem key={c.crm_user_id} value={String(c.crm_user_id)}>
              {c.corretora ?? c.email}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )}
    <span className="text-xs text-muted-foreground">
      {atual.corretora ?? atual.email}
      {atual.susep ? ` · SUSEP ${atual.susep}` : ""}
      {atual.lido_em ? ` · lido em ${new Date(atual.lido_em).toLocaleString("pt-BR")}` : ""}
    </span>
  </div>
);

export const SemConexao = () => (
  <Card>
    <CardContent className="space-y-1 pt-6 text-sm">
      <p>Nenhuma conexão com o portal da Azos.</p>
      <p className="text-muted-foreground">
        Conexão em <Link to="/settings" className="underline">Meu perfil › Conexões</Link>.
      </p>
    </CardContent>
  </Card>
);

export const AvisoConexao = ({ conexao }: { conexao: ConexaoPortal }) =>
  conexao.situacao === "conectado" ? null : (
    <Card>
      <CardContent className="pt-6 text-sm text-destructive">
        {conexao.situacao === "login_expirado" ? "Login expirado. Conectar de novo em Meu perfil › Conexões." : conexao.erro}
      </CardContent>
    </Card>
  );

/** Lê o portal de novo com o login de quem está logado. */
export const BotaoAtualizarAzos = ({ aoConcluir }: { aoConcluir: () => void }) => {
  const notify = useNotify();
  const [atualizando, setAtualizando] = useState(false);
  const atualizar = async () => {
    setAtualizando(true);
    const { erro } = await lerAzosPortal();
    setAtualizando(false);
    if (erro) notify(erro, { type: "error" });
    else notify("Portal da Azos atualizado.");
    aoConcluir();
  };
  return (
    <Button type="button" size="sm" variant="secondary" onClick={atualizar} disabled={atualizando}>
      <RefreshCw className={`mr-1.5 h-4 w-4 ${atualizando ? "animate-spin" : ""}`} />
      {atualizando ? "Atualizando..." : "Atualizar"}
    </Button>
  );
};
